/**
 * MensagensPage Component
 * 
 * Página principal do sistema de mensagens interno do ERP.
 * Permite comunicação direta entre usuários do sistema.
 * 
 * Funcionalidades:
 * - Lista de conversas
 * - Visualização e envio de mensagens
 * - Criação de novas conversas
 * - Notificações de mensagens não lidas
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { 
  MessageSquare, Search, Plus, Send, ArrowLeft, X,
  CheckCheck, Sparkles
} from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { useToast } from '../../../hooks/useToast'
import { messageService } from '../../../services/erpApi'
import Avatar from '../../../components/Avatar/Avatar'
import { Toast } from '../../../components'
import './MensagensPage.css'

function MensagensPage() {
  const { user } = useAuth()
  const { toast, showToast, hideToast } = useToast()
  
  // Estados principais
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [activeTab, setActiveTab] = useState('all') // 'all' | 'unread' | 'automatic'
  const [searchTerm, setSearchTerm] = useState('')
  
  // Estados do formulário
  const [newMessage, setNewMessage] = useState('')
  
  // Estados do modal
  const [isNewMessageModalOpen, setIsNewMessageModalOpen] = useState(false)
  const [availableUsers, setAvailableUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [newRecipientIds, setNewRecipientIds] = useState([])
  const [newSubject, setNewSubject] = useState('')
  const [newInitialMessage, setNewInitialMessage] = useState('')
  
  // Refs
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)

  // Carregar conversas
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true)
      const filters = {
        unreadOnly: activeTab === 'unread',
        automaticOnly: activeTab === 'automatic',
        search: searchTerm || undefined
      }
      const response = await messageService.getConversations(filters)
      if (response.success) {
        setConversations(response.data || [])
      } else {
        showToast(response.message || 'Erro ao carregar conversas', 'error')
      }
    } catch (error) {
      console.error('Erro ao carregar conversas:', error)
      showToast('Erro ao carregar conversas', 'error')
    } finally {
      setLoading(false)
    }
  }, [activeTab, searchTerm, showToast])

  // Carregar mensagens de uma conversa
  const loadMessages = useCallback(async (conversationId) => {
    try {
      const response = await messageService.getMessages(conversationId)
      if (response.success) {
        setMessages(response.data || [])
        // Marcar como lida
        await messageService.markConversationAsRead(conversationId)
        // Atualizar lista de conversas
        loadConversations()
      } else {
        showToast(response.message || 'Erro ao carregar mensagens', 'error')
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error)
      showToast('Erro ao carregar mensagens', 'error')
    }
  }, [loadConversations, showToast])

  // Carregar usuários disponíveis para nova mensagem
  // Mostrar apenas admins e usuários com grupo de acesso válido
  // Usa rota específica de mensagens que não requer permissão PEOPLE_USERS
  const loadAvailableUsers = useCallback(async () => {
    try {
      setLoadingUsers(true)
      const response = await messageService.getAvailableUsers()
      if (response.success) {
        // Filtrar o usuário atual
        const filtered = (response.data || []).filter(u => u.id !== user?.id)
        setAvailableUsers(filtered)
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
    } finally {
      setLoadingUsers(false)
    }
  }, [user?.id])

  // Formatar nome: primeiro nome + primeira letra do sobrenome + "."
  const formatUserName = useCallback((name) => {
    if (!name) return ''
    const parts = name.trim().split(' ')
    if (parts.length === 1) return parts[0]
    const firstName = parts[0]
    const lastNameInitial = parts[parts.length - 1][0].toUpperCase()
    return `${firstName} ${lastNameInitial}.`
  }, [])

  // Selecionar conversa
  const handleSelectConversation = useCallback(async (conversationId) => {
    setSelectedConversation(conversationId)
    await loadMessages(conversationId)
  }, [loadMessages])

  // Enviar mensagem
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return

    try {
      setSending(true)
      const response = await messageService.sendMessage(selectedConversation, newMessage.trim())
      if (response.success) {
        setNewMessage('')
        // Recarregar mensagens
        await loadMessages(selectedConversation)
        // Recarregar conversas para atualizar última mensagem
        await loadConversations()
      } else {
        showToast(response.message || 'Erro ao enviar mensagem', 'error')
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error)
      showToast('Erro ao enviar mensagem', 'error')
    } finally {
      setSending(false)
    }
  }

  // Criar nova conversa
  const handleCreateConversation = async (e) => {
    e.preventDefault()
    
    if (!newSubject.trim() || !newInitialMessage.trim() || newRecipientIds.length === 0) {
      showToast('Preencha todos os campos', 'warning')
      return
    }

    try {
      setSending(true)
      const response = await messageService.createConversation({
        subject: newSubject.trim(),
        participantIds: newRecipientIds,
        initialMessage: newInitialMessage.trim()
      })
      
      if (response.success) {
        setIsNewMessageModalOpen(false)
        setNewSubject('')
        setNewInitialMessage('')
        setNewRecipientIds([])
        // Recarregar conversas
        await loadConversations()
        // Selecionar a nova conversa
        if (response.data?.conversation?.id) {
          await handleSelectConversation(response.data.conversation.id)
        }
        showToast('Conversa criada com sucesso!', 'success')
      } else {
        showToast(response.message || 'Erro ao criar conversa', 'error')
      }
    } catch (error) {
      console.error('Erro ao criar conversa:', error)
      showToast('Erro ao criar conversa', 'error')
    } finally {
      setSending(false)
    }
  }

  // Scroll automático para última mensagem
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  // Carregar conversas ao montar e quando filtros mudarem
  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  // Polling para atualizar mensagens automaticamente (simula mensagens instantâneas)
  useEffect(() => {
    if (!selectedConversation) return

    const interval = setInterval(async () => {
      // Recarregar mensagens da conversa ativa
      await loadMessages(selectedConversation)
      // Recarregar lista de conversas para atualizar última mensagem
      await loadConversations()
    }, 5000) // Atualizar a cada 5 segundos

    return () => clearInterval(interval)
  }, [selectedConversation, loadMessages, loadConversations])

  // Carregar usuários quando modal abrir
  useEffect(() => {
    if (isNewMessageModalOpen) {
      loadAvailableUsers()
    }
  }, [isNewMessageModalOpen, loadAvailableUsers])

  // Formatar timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Agora'
    if (minutes < 60) return `${minutes}m`
    if (hours < 24) return `${hours}h`
    if (days < 7) return `${days}d`
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  }

  const activeConv = conversations.find(c => c.id === selectedConversation)

  return (
    <div className="mensagens-page">
      {/* Sidebar: Lista de Conversas */}
      <div className={`mensagens-sidebar ${selectedConversation ? 'hidden lg:flex' : 'flex'}`}>
        <div className="mensagens-sidebar-header">
          <div className="mensagens-sidebar-title-row">
            <h1 className="mensagens-sidebar-title">Mensagens</h1>
            <button 
              onClick={() => setIsNewMessageModalOpen(true)}
              className="mensagens-btn-new"
              title="Nova mensagem"
            >
              <Plus size={20} />
            </button>
          </div>
          
          <div className="mensagens-search-wrapper">
            <Search className="mensagens-search-icon" size={16} />
            <input 
              type="text" 
              placeholder="Pesquisar por assunto..."
              className="mensagens-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="mensagens-tabs">
            <button 
              onClick={() => setActiveTab('all')}
              className={`mensagens-tab ${activeTab === 'all' ? 'active' : ''}`}
            >
              Todas
            </button>
            <button 
              onClick={() => setActiveTab('automatic')}
              className={`mensagens-tab ${activeTab === 'automatic' ? 'active' : ''}`}
            >
              Automáticas
            </button>
            <button 
              onClick={() => setActiveTab('unread')}
              className={`mensagens-tab ${activeTab === 'unread' ? 'active' : ''}`}
            >
              Não Lidas
            </button>
          </div>
        </div>

        <div className="mensagens-conversations-list">
          {loading ? (
            <div className="mensagens-loading">Carregando...</div>
          ) : conversations.length === 0 ? (
            <div className="mensagens-empty">
              <MessageSquare size={48} />
              <p>Nenhuma conversa encontrada</p>
            </div>
          ) : (
            conversations.map(conv => (
              <button
                key={conv.id}
                onClick={() => handleSelectConversation(conv.id)}
                className={`mensagens-conversation-item ${
                  selectedConversation === conv.id ? 'active' : ''
                }`}
              >
                <div className="mensagens-conversation-header">
                  <span className="mensagens-conversation-subject">#{conv.subject}</span>
                  <span className="mensagens-conversation-time">
                    {formatTimestamp(conv.last_message_at || conv.updated_at)}
                  </span>
                </div>
                <div className="mensagens-conversation-content">
                  {conv.participants && conv.participants.length > 0 && (
                    <Avatar 
                      name={conv.participants[0].name} 
                      imageUrl={conv.participants[0].avatar_url}
                      size="sm"
                    />
                  )}
                  <div className="mensagens-conversation-info">
                    <div className="mensagens-conversation-name-row">
                      <p className="mensagens-conversation-name">
                        {conv.participants && conv.participants.length > 0 
                          ? conv.participants[0].name 
                          : 'Usuário'}
                      </p>
                      {conv.is_automatic && (
                        <span className="mensagens-conversation-automatic-tag">
                          <Sparkles size={10} />
                          Higeia
                        </span>
                      )}
                    </div>
                    <p className="mensagens-conversation-preview">
                      {conv.last_message_content || 'Sem mensagens'}
                    </p>
                  </div>
                  {conv.unread_count > 0 && (
                    <div className="mensagens-unread-badge" />
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={`mensagens-chat ${selectedConversation ? 'flex' : 'hidden lg:flex'}`}>
        {activeConv ? (
          <>
            {/* Chat Header */}
            <div className="mensagens-chat-header">
              <div className="mensagens-chat-header-left">
                <button 
                  onClick={() => setSelectedConversation(null)}
                  className="mensagens-btn-back lg:hidden"
                >
                  <ArrowLeft size={20} />
                </button>
                {activeConv.participants && activeConv.participants.length > 0 && (
                  <>
                    <Avatar 
                      name={activeConv.participants[0].name}
                      imageUrl={activeConv.participants[0].avatar_url}
                      size="md"
                    />
                    <div className="mensagens-chat-header-info">
                      <div className="mensagens-chat-title-row">
                        <h2 className="mensagens-chat-title">#{activeConv.subject}</h2>
                        {activeConv.is_automatic && (
                          <span className="mensagens-chat-automatic-tag">
                            <Sparkles size={12} />
                            Automática
                          </span>
                        )}
                      </div>
                      <p className="mensagens-chat-subtitle">
                        {activeConv.participants[0].name}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Messages Area */}
            <div className="mensagens-messages-area" ref={messagesContainerRef}>
              <div className="mensagens-messages-list">
                {messages.length === 0 ? (
                  <div className="mensagens-messages-empty">
                    <span>Início da Conversa Profissional</span>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMe = msg.sender_id === user?.id
                    const isAutomatic = msg.is_automatic || false
                    return (
                      <div key={msg.id} className={`mensagens-message ${isMe ? 'own' : 'other'} ${isAutomatic ? 'automatic' : ''}`}>
                        <div className={`mensagens-message-bubble ${isMe ? 'own' : 'other'} ${isAutomatic ? 'automatic' : ''}`}>
                          {isAutomatic && (
                            <div className="mensagens-message-automatic-badge">
                              <Sparkles size={12} />
                              <span>Higeia</span>
                            </div>
                          )}
                          <p className="mensagens-message-content">{msg.content}</p>
                        </div>
                        <div className="mensagens-message-meta">
                          <span className="mensagens-message-time">
                            {formatTimestamp(msg.created_at)}
                          </span>
                          {isMe && !isAutomatic && <CheckCheck size={12} className="mensagens-message-read" />}
                        </div>
                      </div>
                    )
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Message Input */}
            <div className="mensagens-input-area">
              <div className="mensagens-input-wrapper">
                <textarea
                  rows={1}
                  placeholder="Mensagem..."
                  className="mensagens-input"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage()
                    }
                  }}
                  disabled={sending}
                />
                <button
                  onClick={handleSendMessage}
                  className={`mensagens-btn-send ${newMessage.trim() ? 'active' : ''}`}
                  disabled={!newMessage.trim() || sending}
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="mensagens-chat-empty">
            <div className="mensagens-chat-empty-icon">
              <MessageSquare size={64} />
              <Sparkles className="mensagens-chat-empty-sparkle" size={24} />
            </div>
            <h3 className="mensagens-chat-empty-title">Medik Messenger</h3>
            <p className="mensagens-chat-empty-text">
              Selecione uma conversa ao lado para visualizar o histórico de mensagens e coordenar suas operações B2B.
            </p>
          </div>
        )}
      </div>

      {/* Modal: Nova Mensagem */}
      {isNewMessageModalOpen && (
        <div className="mensagens-modal-overlay" onClick={() => setIsNewMessageModalOpen(false)}>
          <div className="mensagens-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mensagens-modal-header">
              <h3 className="mensagens-modal-title">Nova Mensagem</h3>
              <button 
                onClick={() => setIsNewMessageModalOpen(false)}
                className="mensagens-modal-close"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreateConversation} className="mensagens-modal-form">
              <div className="mensagens-modal-field">
                <label className="mensagens-modal-label">Destinatário(s)</label>
                <div className="mensagens-recipients-list">
                  {loadingUsers ? (
                    <div className="mensagens-recipients-loading">Carregando...</div>
                  ) : availableUsers.length === 0 ? (
                    <div className="mensagens-recipients-empty">Nenhum usuário disponível</div>
                  ) : (
                    availableUsers.map(u => {
                      const isSelected = newRecipientIds.includes(u.id)
                      return (
                        <div
                          key={u.id}
                          className={`mensagens-recipient-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setNewRecipientIds(newRecipientIds.filter(id => id !== u.id))
                            } else {
                              setNewRecipientIds([...newRecipientIds, u.id])
                            }
                          }}
                        >
                          <Avatar
                            imageUrl={u.avatar_url}
                            name={u.name}
                            size="md"
                          />
                          <span className="mensagens-recipient-name">
                            {formatUserName(u.name)}
                          </span>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>

              <div className="mensagens-modal-field">
                <label className="mensagens-modal-label">Assunto</label>
                <input
                  type="text"
                  placeholder="Ex: Reunião de Planejamento"
                  className="mensagens-modal-input"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  required
                />
              </div>

              <div className="mensagens-modal-field">
                <label className="mensagens-modal-label">Mensagem Inicial</label>
                <textarea
                  rows={4}
                  placeholder="Digite sua mensagem..."
                  className="mensagens-modal-textarea"
                  value={newInitialMessage}
                  onChange={(e) => setNewInitialMessage(e.target.value)}
                  required
                />
              </div>

              <div className="mensagens-modal-actions">
                <button
                  type="button"
                  onClick={() => setIsNewMessageModalOpen(false)}
                  className="mensagens-modal-btn-cancel"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="mensagens-modal-btn-submit"
                  disabled={sending}
                >
                  {sending ? 'Enviando...' : 'Enviar Mensagem'} <Send size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />
    </div>
  )
}

export default MensagensPage
